import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RegionProvider } from "@/context/RegionContext";
import Shell from "@/components/layout/Shell";
import SuperAdminShell from "@/components/layout/SuperAdminShell";
import PostLoginLoader from "@/components/common/PostLoginLoader";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import RegisterDevice from "@/pages/RegisterDevice";
import DeviceDetail from "@/pages/DeviceDetail";
import Reports from "@/pages/Reports";
import History from "@/pages/History";
import TrackOrders from "@/pages/TrackOrders";
import Settings from "@/pages/Settings";
import AdminOverview from "@/pages/admin/AdminOverview";
import Tenants from "@/pages/admin/Tenants";
import AdminDevices from "@/pages/admin/AdminDevices";
import AdminDeviceDetail from "@/pages/admin/AdminDeviceDetail";
import AdminHistory from "@/pages/admin/AdminHistory";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSettings from "@/pages/admin/AdminSettings";

function Loading() {
  return <div className="h-full bg-kl-black flex items-center justify-center kl-label-dark">Establishing link…</div>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function TenantRoutes() {
  return (
    <RegionProvider>
      <Shell />
    </RegionProvider>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.role === "super_admin") {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<SuperAdminShell />}>
          <Route path="/" element={<AdminOverview />} />
          <Route path="/tenants" element={<Tenants />} />
          <Route path="/devices" element={<AdminDevices />} />
          <Route path="/devices/:id" element={<AdminDeviceDetail />} />
          <Route path="/history" element={<AdminHistory />} />
          <Route path="/orders" element={<AdminOrders />} />
          <Route path="/settings" element={<AdminSettings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route element={<TenantRoutes />}>
        <Route path="/" element={<Overview />} />
        <Route path="/register-device" element={<RegisterDevice />} />
        <Route path="/devices/:id" element={<DeviceDetail />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/:id" element={<Reports />} />
        <Route path="/history" element={<History />} />
        <Route path="/orders" element={<TrackOrders />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  const { justLoggedIn } = useAuth();
  return (
    <>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        // sonner styles its title/description text off these CSS variables
        // internally — setting only toastOptions.style (an inline style on
        // the outer toast element) doesn't reach those nested elements, so
        // the description was rendering in sonner's default gray regardless.
        style={{ "--normal-bg": "#050505", "--normal-text": "#fff", "--normal-border": "#262626" }}
        toastOptions={{ style: { background: "#050505", color: "#fff", border: "1px solid #262626", borderRadius: 0, fontFamily: "Chivo, sans-serif" } }}
      />
      {justLoggedIn && <PostLoginLoader />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </div>
  );
}
